/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { useState, useMemo } from "react";

import { format as formatFns, getMonth, getYear, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { months, calendarSizesClasses } from "./settings";
import { parseDateLocal } from "@/utils/timeUtils";
import { formatDateToStandarizedForm } from "@/utils/calendarUtils";
import { CalendarCell } from "./CalendarCell";
import { CHANGE_DAYS } from "@/utils/calendarUtils";
import RotatLogo from "@/assets/logo-small.svg";
import { 
    type CrewInterface,
} from "./types";

type Props = {
    crewMember: CrewInterface;
    calendarZoomLevel: number;
    startDate: string | null | undefined;
    endDate: string | null | undefined;
    calendarGeneralAdditionalData: any;
    onClickDayHandler: ({ day, crewMemberData }: {
        day: {
            date: string;
            type: string;
        };
        crewMemberData: {
            id: number;
            first_name: string;
            last_name: string;
            rotation: string;
            position_id: number;
            partner_id: number;
        };
    }) => void;
    moveChangeDay: {
        from: null | string;
        to: null | string;
    };
    crewMemberData: {
        id: number;
        first_name: string;
        last_name: string;
        rotation: string;
        position_id: number;
        partner_id: number;
    } | null | undefined;
    onClickDocumentHandler: ({ documentBatchData, userId }: {
        documentBatchData: any;
        userId: string;
    }) => void;
};

const CalendarRow = ({
    crewMember,
    calendarZoomLevel,
    startDate,
    endDate,
    calendarGeneralAdditionalData,
    onClickDayHandler,
    moveChangeDay,
    crewMemberData,
    onClickDocumentHandler,
}: Props) => {
    const [hoveredCell, setHoveredCell] = useState<string | undefined>();
    

    const getDaysInMonth = (month: number | string, year: number | string) => {
		const monthNum = Number(month);
		const yearNum = Number(year);
		const daysInMonths = [31, (yearNum % 4 === 0 && (yearNum % 100 !== 0 || yearNum % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
		return daysInMonths[monthNum - 1];
	}

    const getMonthName = (month: number, months: {
		label: string;
		value: number;
	}[]) => {
		return months.find( _month => _month.value === month)?.label;
	}

    const calendarMonthsData = useMemo(() => {
        if(!startDate || !endDate) return [];

        const d_startDate = parseDateLocal(startDate);
        const d_endDate = parseDateLocal(endDate);

        const result = [];

        let currentDate = d_startDate;

        while(currentDate <= d_endDate) {
            const currentMonth = getMonth(currentDate) + 1;
            const currentYear = getYear(currentDate);

            result.push({
                days: getDaysInMonth(currentMonth, currentYear),
                month: currentMonth,
                year: currentYear,
                name: getMonthName(currentMonth, months)
            });
            currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        }

        return result;
    }, [startDate, endDate]);

    const isDateInRangeOfDates = (
        {
            currentDate, 
            startDate, 
            hoveredCell
        }: {
            currentDate: string | undefined,
            startDate: string | undefined, 
            hoveredCell: string | undefined
        }
    ) => {
        if(!startDate || !hoveredCell || !currentDate) return false;
        const current = parseDateLocal(currentDate);
        const start = parseDateLocal(startDate);
        const hovered = parseDateLocal(hoveredCell);

        if (isNaN(current) || isNaN(start) || isNaN(hovered)) return false;
        const min = new Date(Math.min(hovered.getTime(), start.getTime()));
        const max = new Date(Math.max(hovered.getTime(), start.getTime()));
        return current >= min && current <= max;
    }

    const generateMonthCells = ({crewMember, calendarData} : {
        crewMember: {
            id: number;
            first_name: string;
            last_name: string;
            rotation: string;
            position_id: number;
            partner_id: number;
        };
        calendarData: {
            duration: number;
            start_date: string;
            type: string;
        }[]
    }) => {
        if(!calendarData) return null;

        return calendarData.map((day: {
            duration: number;
            start_date: string;
            type: string;
        }) => {
            return (
                [...Array(day.duration)].map((_, index) => {
                    const date = addDays(parseDateLocal(formatDateToStandarizedForm(day.start_date)), index);
                    const formattedDate = formatFns(date, "y-MM-dd");
                    const extrudeData = calendarGeneralAdditionalData?.data?.positions?.[crewMember.position_id]?.[crewMember.id]?.[formattedDate];

                    return (
                        <CalendarCell 
                            key={index}
                            zoomLevel={calendarZoomLevel}
                            label={CHANGE_DAYS.includes(day.type) && (
                                <img src={RotatLogo} alt="Rotat Logo" className={cn("inline-block", calendarSizesClasses[calendarZoomLevel].changeLogo)}/>
                            )}
                            onClick={() => onClickDayHandler({
                                day: {
                                    date: formattedDate, 
                                    type: day.type
                                },
                                crewMemberData: crewMember
                            })}

                            type={ (
                                (formattedDate === moveChangeDay.from) && (crewMemberData.id === crewMember?.id)) 
                                ? "highlight" 
                                :(isDateInRangeOfDates({
                                    currentDate: formattedDate, 
                                    startDate: moveChangeDay.from, 
                                    hoveredCell
                                }) && crewMemberData.id === crewMember?.id) ? "hovered" 
                                : day.type
                            }

                            onClickDocument={onClickDocumentHandler}

                            date={date}
                            showTooltip={true}

                            notesIds={ extrudeData?.notes || [] }
                            documentsIds={ extrudeData?.documents || [] }

                            crewMemberData={[{
                                id: crewMember.id,
                                position_id: crewMember.position_id
                            }]}

                            setHoveredCell={setHoveredCell}
                        />
                    )
                })
            );
        });
    }

    return (
        <div key={crewMember.id} className="mb-1 flex">
            {
                calendarMonthsData?.map((calendarMonthData) => (
                    <div 
                        className={cn(`
                            grow flex gap-px px-2 mb-1 border-e 
                            first:ps-0 last:border-none last:pe-0`,
                            calendarSizesClasses[calendarZoomLevel].gap
                        )}
                        key={calendarMonthData.name}
                    >
                        {generateMonthCells({crewMember: crewMember ,calendarData: crewMember.calendar[`${calendarMonthData.year}-${calendarMonthData.month}`]})}
                    </div>
                ))
            }
        </div>
    )
}

export {
    CalendarRow,
}